/** Credit plan marketing copy (pricing comes from /billing/plans API). */

export const CREDIT_PLAN_CATALOG = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Perfect for your first narrated stories',
    recommended: false,
    benefits: [
      'AI storyboard images & narration TTS',
      'Slideshow video export & scene editor',
      'Structured script + word timestamps',
      'Advanced analytics: compliance, SEO, and ranking timeline',
      'Enough for ~5–8 short narration projects',
    ],
  },
  {
    id: 'creator',
    name: 'Creator',
    tagline: 'Best for regular YouTube & Reels creators',
    recommended: true,
    benefits: [
      'Everything in Starter',
      'AI video — MiniMax, Runway, Google Veo',
      'Full automation: audio → video → export',
      'YouTube & Instagram auto-publish',
      'Advanced analytics: compliance, SEO, and ranking timeline',
      'Enough for ~15–20 polished videos/month',
    ],
  },
  {
    id: 'studio',
    name: 'Studio',
    tagline: 'Teams, agencies & high-volume production',
    recommended: false,
    benefits: [
      'Everything in Creator',
      'Background music generation (whole story)',
      'Bulk scene regenerate & priority jobs',
      'News & multi-path story workflows',
      'Enough for ~50+ videos/month at scale',
    ],
  },
  {
    id: 'custom',
    name: 'Custom plan',
    custom: true,
    tagline: 'Volume pricing tailored to your pipeline',
    recommended: false,
    benefits: [
      'Custom credit pools & invoicing',
      'Dedicated onboarding & workflow design',
      'API access & enterprise integrations',
      'SLA, priority support & training',
    ],
    ctaLabel: 'Talk to sales',
    ctaPath: '/partners/creative',
  },
];

/** @deprecated use CREDIT_PLAN_CATALOG */
export const CREDIT_PLANS = CREDIT_PLAN_CATALOG;

const catalogById = Object.fromEntries(CREDIT_PLAN_CATALOG.map((p) => [p.id, p]));

const FALLBACK_ORIGINAL_INR = {
  starter: 499,
  creator: 999,
  studio: 2499,
};

const UI_PLAN_CREDITS = {
  starter: 499,
  creator: 999,
  studio: 2499,
};

export function mergeCreditPlans(apiPlans = [], creditsPerInr = 2) {
  const pricingById = Object.fromEntries((apiPlans || []).map((p) => [p.id, p]));
  const purchasable = CREDIT_PLAN_CATALOG.filter((p) => !p.custom).map((meta) => {
    const pricing = pricingById[meta.id];
    const originalPriceInr = pricing?.original_price_inr ?? FALLBACK_ORIGINAL_INR[meta.id] ?? 0;
    const offerPriceInr = pricing?.offer_price_inr ?? originalPriceInr / 2;
    const credits = UI_PLAN_CREDITS[meta.id] ?? pricing?.credits ?? Math.round(originalPriceInr);
    return {
      ...meta,
      originalPriceInr,
      offerPriceInr,
      credits,
      discountPercent: pricing?.discount_percent ?? Math.round((1 - offerPriceInr / originalPriceInr) * 100),
      active: pricing?.active !== false,
      amountInr: offerPriceInr,
    };
  });

  const custom = CREDIT_PLAN_CATALOG.filter((p) => p.custom);
  return [...purchasable.filter((p) => p.active !== false), ...custom];
}

export function creditsForInr(amountInr, creditsPerInr = 2) {
  const amt = Number(amountInr);
  const cpi = Number(creditsPerInr) || 2;
  if (!Number.isFinite(amt) || amt <= 0) return 0;
  return Math.round(amt * cpi * 100) / 100;
}

export function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: n % 1 ? 2 : 0 })}`;
}

export function parseInsufficientCreditsDetail(message) {
  const text = String(message || '');
  const neededMatch = text.match(/needs about ([\d.]+) credits/i)
    || text.match(/needs ([\d.]+) credits/i);
  const balanceMatch = text.match(/you have ([\d.]+)/i)
    || text.match(/balance ([\d.]+)/i);
  const segmentsMatch = text.match(/\((\d+) segments?\)/i);
  return {
    neededCredits: neededMatch ? parseFloat(neededMatch[1]) : null,
    balance: balanceMatch ? parseFloat(balanceMatch[1]) : null,
    segmentCount: segmentsMatch ? parseInt(segmentsMatch[1], 10) : null,
  };
}

export function isInsufficientCreditsError(err) {
  return err?.response?.status === 402;
}

export function getPlanCatalogEntry(planId) {
  return catalogById[planId] || null;
}
